import { Component, Input, inject, ViewChild, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { LocationFilterComponent } from '../location-filter/location-filter.component';
import { LoginModalComponent } from '../login-modal/login-modal.component';
import { UserSidebarComponent } from '../user-sidebar/user-sidebar.component';
import { AuthService } from '../../../core/services/auth.service';
import { PlatformService } from '../../../core/services/platform.service';
import { environment } from '../../../../environments/environment';

export interface HeaderConfig {
  showLogo?: boolean;
  showNavigation?: boolean;
  showUserActions?: boolean;
  showMobileMenu?: boolean;
  showLocationFilter?: boolean;
  customTitle?: string;
  customSubtitle?: string;
  backgroundColor?: string;
  textColor?: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    LocationFilterComponent,
    LoginModalComponent,
    UserSidebarComponent,
  ],
  template: `
    <nav
      class="bg-white border-gray-200 sticky top-0 z-40"
      [class]="effectiveConfig.backgroundColor"
    >
      <div class="max-w-screen-xl mx-auto px-4 py-2">
        <!-- Desktop Layout: Logo left, Location Filter and User Actions right -->
        <div class="hidden md:flex items-center justify-between gap-4">
          <!-- Logo (left side) -->
          <div *ngIf="effectiveConfig.showLogo" class="flex-shrink-0">
            <a routerLink="/offers" class="flex items-center">
              <img
                src="/gastronauta_logo_con_texto_lateral.svg"
                alt="Gastronauta"
                class="h-12 w-auto"
              />
            </a>
          </div>

          <!-- Right side: Location Filter and User Actions -->
          <div class="flex items-center gap-4 flex-1 justify-end">
            <!-- Location Filter (right side on desktop) -->
            <div
              class="flex-shrink-0"
              *ngIf="effectiveConfig.showLocationFilter"
            >
              <app-location-filter></app-location-filter>
            </div>

            <!-- User Actions (right side) -->
            <div
              class="flex items-center gap-3 flex-shrink-0"
              *ngIf="effectiveConfig.showUserActions"
            >
              <!-- Show user profile or login button -->
              <ng-container
                *ngIf="authService.isAuthenticated(); else loginButton"
              >
                <!-- User Profile Button -->
                <button
                  type="button"
                  (click)="openUserSidebar()"
                  class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors cursor-pointer"
                  [attr.aria-label]="'sidebar.openMenu' | translate"
                >
                  <i class="fas fa-user text-lg"></i>
                  <span class="hidden sm:inline">
                    {{ authService.currentUser()?.name || 'Usuario' }}
                  </span>
                </button>
              </ng-container>

              <!-- Login/Settings Button Template -->
              <ng-template #loginButton>
                <button
                  type="button"
                  (click)="openUserSidebar()"
                  class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors cursor-pointer"
                  [attr.aria-label]="showLoginOption ? ('auth.login' | translate) : ('nav.settings' | translate)"
                >
                  <i class="fas fa-user text-lg"></i>
                  <span>{{
                    showLoginOption ? ('auth.login' | translate) : ('nav.settings' | translate)
                  }}</span>
                </button>
              </ng-template>
            </div>
          </div>
        </div>

        <!-- Mobile Layout: Logo top, Location Filter below, User Actions right -->
        <div class="md:hidden flex flex-col gap-2">
          <!-- Top row: Logo and User Actions -->
          <div class="flex items-center justify-between">
            <!-- Logo (left side) -->
            <div *ngIf="effectiveConfig.showLogo" class="flex-shrink-0">
              <a routerLink="/offers" class="flex items-center">
                <img
                  src="/gastronauta_logo_con_texto_lateral.svg"
                  alt="Gastronauta"
                  class="h-10 w-auto"
                />
              </a>
            </div>

            <!-- User Actions (right side) -->
            <div
              class="flex items-center gap-3 flex-shrink-0"
              *ngIf="effectiveConfig.showUserActions"
            >
              <!-- Show user profile or login button -->
              <ng-container
                *ngIf="authService.isAuthenticated(); else loginButtonMobile"
              >
                <!-- User Profile Button -->
                <button
                  type="button"
                  (click)="openUserSidebar()"
                  class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors cursor-pointer"
                  [attr.aria-label]="'sidebar.openMenu' | translate"
                >
                  <i class="fas fa-user text-lg"></i>
                </button>
              </ng-container>

              <!-- Login Button Template (Mobile) -->
              <ng-template #loginButtonMobile>
                <button
                  type="button"
                  (click)="openUserSidebar()"
                  class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors cursor-pointer"
                  [attr.aria-label]="'nav.settings' | translate"
                >
                  <i class="fas fa-user text-lg"></i>
                  <span>{{ 'nav.settings' | translate }}</span>
                </button>
              </ng-template>
            </div>
          </div>

          <!-- Location Filter (below on mobile) -->
          <div class="w-full" *ngIf="effectiveConfig.showLocationFilter">
            <app-location-filter></app-location-filter>
          </div>
        </div>
      </div>
    </nav>

    <!-- Login Modal -->
    <app-login-modal #loginModal></app-login-modal>

    <!-- User Sidebar -->
    <app-user-sidebar
      [isOpen]="isUserSidebarOpen()"
      (close)="closeUserSidebar()"
    ></app-user-sidebar>
  `,
  styles: [
    `
      /* Host element must be block for flex layout */
      :host {
        display: block;
        flex-shrink: 0;
      }

      nav {
        box-shadow:
          0 1px 3px 0 rgba(0, 0, 0, 0.1),
          0 1px 2px 0 rgba(0, 0, 0, 0.06);
        /* Safe area inset for iOS/Android native status bar */
        /* Uses CSS variables from global styles.scss (supports both env() and constant()) */
        padding-top: var(--safe-area-inset-top, env(safe-area-inset-top, 0px));
        padding-left: var(
          --safe-area-inset-left,
          env(safe-area-inset-left, 0px)
        );
        padding-right: var(
          --safe-area-inset-right,
          env(safe-area-inset-right, 0px)
        );
        /* Prevent iOS overscroll past header */
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
        /* Ensure header stays at top even during overscroll */
        will-change: transform;
      }

      button:hover i {
        transform: scale(1.1);
        transition: transform 0.2s;
      }
    `,
  ],
})
export class HeaderComponent {
  @Input() config: HeaderConfig = {};
  @ViewChild('loginModal') loginModal?: LoginModalComponent;

  authService = inject(AuthService);
  private platformService = inject(PlatformService);

  // Platform detection - hide login text on native mobile apps
  isNativePlatform = this.platformService.isNative();

  // Only show login option when Google Sign-In is available and not on native
  showLoginOption = !this.isNativePlatform && (environment.googleSignInEnabled ?? false);

  // User sidebar state
  isUserSidebarOpen = signal(false);

  // Default configuration
  private defaultConfig: HeaderConfig = {
    showLogo: true,
    showNavigation: true,
    showUserActions: true,
    showMobileMenu: false,
    showLocationFilter: true,
    backgroundColor: 'bg-white',
    textColor: 'text-gray-900',
  };

  get effectiveConfig(): HeaderConfig {
    return { ...this.defaultConfig, ...this.config };
  }

  /**
   * Open login modal
   */
  openLoginModal(): void {
    this.loginModal?.open();
  }

  /**
   * Open user sidebar
   */
  openUserSidebar(): void {
    this.isUserSidebarOpen.set(true);
    // Prevent body scroll when sidebar is open
    document.body.style.overflow = 'hidden';
  }

  /**
   * Close user sidebar
   */
  closeUserSidebar(): void {
    this.isUserSidebarOpen.set(false);
    // Restore body scroll
    document.body.style.overflow = '';
  }
}
